import { Inject, Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { WINDOW } from '@shared/providers/window.provider';
import { EnvService } from '@shared/services/env.service';
import { Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '@shared/services/auth.service';
import { OAuthProvider } from '../../types/oauth2';
import { mergeOAuthConfig } from '../../oauth.config';
import { SignInResponse } from '../../types/auth';

@Injectable({
  providedIn: 'root',
})
export class GoogleAuthService implements OAuthProvider {
  private readonly redirectUri = this.window.location.origin;
  constructor(
    @Inject(WINDOW) private readonly window: Window,
    private readonly oauth: OAuthService,
    private readonly env: EnvService,
    private readonly router: Router,
    private readonly auth: AuthService,
  ) {
    this.configure();
  }

  signIn() {
    this.oauth.initCodeFlow();
  }

  configure() {
    const config = this.env.getGoogleOAuthConfig();
    this.oauth.configure(
      mergeOAuthConfig({ ...config, redirectUri: this.redirectUri }),
    );
  }

  getAuthenticationCodes() {
    const urlParams = new URLSearchParams(this.window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const codeVerifier = this.window.sessionStorage.getItem('PKCE_verifier');
    if (code && state && codeVerifier) {
      return { code, codeVerifier };
    }
    return null;
  }

  clearGoogleOAuthQueryParams(): void {
    const currentPath = this.router.url;
    this.router.navigate([currentPath], {
      queryParams: {
        code: null,
        state: null,
        scope: null,
        authuser: null,
        session_state: null,
        prompt: null,
        hd: null,
        error: null,
        error_description: null,
        error_uri: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  trySignInWithQueryParams(): Observable<SignInResponse | null> {
    const codes = this.getAuthenticationCodes();
    if (codes === null) {
      return of(null);
    }
    this.clearGoogleOAuthQueryParams();
    return this.auth.signInWithGoogle({
      ...codes,
      redirectUri: this.redirectUri,
    });
  }
}
